import React from "react";
import { Exercise, EXERCISE_TYPES } from "../../../libs/exercises/types";
import { ProgressionTemplate } from "../../../libs/progression/types";

type Props = {
  exerciseType: string;
  selectedExercise: Exercise | null;
  selectedProgression: ProgressionTemplate | null;
  // SUPERSET
  supersetExercises: {
    exercise: Exercise | null;
    progression: ProgressionTemplate | null;
  }[];
  // Clone options
  exToCloneIndex?: number;
  deleteClone: boolean;
  keepNotes: boolean;
  onToggleDeleteClone: () => void;
  onToggleKeepNotes: () => void;
};

const typeLabels: Record<string, { label: string; color: string; bg: string }> = {
  DEFAULT: { label: "Esercizio", color: "text-blue-700 dark:text-blue-300", bg: "bg-blue-100 dark:bg-blue-900/30" },
  SUPERSET: { label: "Superset", color: "text-purple-700 dark:text-purple-300", bg: "bg-purple-100 dark:bg-purple-900/30" },
  STRIPPING: { label: "Stripping", color: "text-orange-700 dark:text-orange-300", bg: "bg-orange-100 dark:bg-orange-900/30" },
  CHECK: { label: "Check", color: "text-emerald-700 dark:text-emerald-300", bg: "bg-emerald-100 dark:bg-emerald-900/30" },
};

export const StepConfirm: React.FC<Props> = ({
  exerciseType,
  selectedExercise,
  selectedProgression,
  supersetExercises,
  exToCloneIndex,
  deleteClone,
  keepNotes,
  onToggleDeleteClone,
  onToggleKeepNotes,
}) => {
  const isSuperset = exerciseType === EXERCISE_TYPES.SUPERSET;
  const typeInfo = typeLabels[exerciseType] ?? typeLabels.DEFAULT;

  return (
    <div className="flex flex-col items-center justify-center h-full py-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
        Riepilogo
      </h2>

      <div className="w-full max-w-lg space-y-4">
        {/* Type badge */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 dark:text-gray-400 w-24">Tipo</span>
          <span className={`text-sm font-medium px-3 py-1 rounded-full ${typeInfo.bg} ${typeInfo.color}`}>
            {typeInfo.label}
          </span>
        </div>

        {/* Exercise(s) */}
        {isSuperset ? (
          <div>
            <span className="text-sm text-gray-500 dark:text-gray-400 block mb-2">
              Esercizi ({supersetExercises.length})
            </span>
            <div className="space-y-2">
              {supersetExercises.map((se, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-medium mt-0.5">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {se.exercise?.name ?? "—"}
                    </p>
                    {se.exercise?.tags && se.exercise.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {se.exercise.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {se.progression
                        ? `Progressione: ${se.progression.name}`
                        : "Nessuna progressione"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400 w-24 pt-1">
              Esercizio
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {selectedExercise?.name ?? "—"}
              </p>
              {selectedExercise?.tags && selectedExercise.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedExercise.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progression (single) */}
        {!isSuperset && exerciseType !== "STRIPPING" && exerciseType !== "CHECK" && (
          <div className="flex items-start gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400 w-24 pt-1">
              Progressione
            </span>
            <div className="flex-1">
              {selectedProgression ? (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    {selectedProgression.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {selectedProgression.numWeeks} settiman
                    {selectedProgression.numWeeks !== 1 ? "e" : "a"} &middot;{" "}
                    {selectedProgression.weeks[0]?.sets.length ?? 0} set per settimana
                  </p>
                  <div className="flex gap-1 mt-2">
                    {selectedProgression.weeks.slice(0, 12).map((w, i) => (
                      <div
                        key={i}
                        className="flex-1 h-1.5 rounded-full bg-blue-300 dark:bg-blue-700"
                        title={`Settimana ${w.weekNumber}`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                  Nessuna progressione
                </p>
              )}
            </div>
          </div>
        )}

        {/* CHECK info */}
        {exerciseType === "CHECK" && (
          <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
            <p className="text-xs text-emerald-600 dark:text-emerald-400">
              4 set e note verranno pre-compilati automaticamente in tutte le settimane.
            </p>
          </div>
        )}

        {/* Clone options */}
        {typeof exToCloneIndex === "number" && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              Opzioni clone
            </p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteClone}
                  onChange={onToggleDeleteClone}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Elimina originale
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={keepNotes}
                  onChange={onToggleKeepNotes}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Mantieni note
                </span>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
